/**
 * collection-api-filter.js
 * ------------------------------------------------------------
 * 大規模コレクション(例: /collections/all/cl_イエロー+mo_ap2279+h1_sp、
 * および /collections/all のようなタグ無しの一覧表示)を、
 * Liquidの collection.products / all_tags ではなく
 * 既存の window.get_products_graphql (Storefront API) 経由で取得して
 * 表示するモジュール。
 *
 * ※ タグの全文検索(tag:xxx)ベースのため、対象は "all"(ストア全商品)
 *    コレクションのみを想定している。他のコレクション(部分集合)に
 *    適用する場合はスコープが正しく効かない点に注意。
 *
 * 前提:
 *   ・theme_accessToken がページ内で既に定義されていること
 *     (例: <script>const theme_accessToken = '{{ settings.accessToken }}';</script>)
 *   ・window.get_products_graphql が既存の product-detail 用スクリプトで
 *     グローバル公開されていること(そちらを優先して使う)
 *     ※ sortKey に加え、第5引数 reverse (真偽値) も受け取れるように
 *       拡張されている必要がある(後方互換の追加引数)
 *   ・window.fox.settings.model / window.fox.settings.h1 が既存同様
 *     どこかのLiquidから出力されていること
 *
 * 並び替え:
 *   [data-collection-sort] というセレクト要素があればそれを使い、
 *   無ければ簡易的なセレクトをグリッドの直前に自動生成する。
 *   選択肢は SORT_OPTIONS を参照(タイトル順・おすすめ・新着・安い順・高い順)。
 * ------------------------------------------------------------
 */
(function () {
  'use strict';

  const CONFIG = {
    perPage: 24,
    collectionsBasePath: '/collections/',
  };

  // ============================================================
  // 並び替えの選択肢
  //   キー: UIのvalueとして使う識別子
  //   sortKey / reverse: Storefront APIに渡す値
  // ============================================================
  const LANG_CODE = (document.documentElement.lang || 'ja').split('-')[0].toUpperCase();
  const IS_EN = LANG_CODE === 'EN';

  const SORT_OPTIONS = IS_EN
    ? {
      recommended: { label: 'Recommended', sortKey: 'BEST_SELLING', reverse: false },
      popularity: { label: 'Popularity', sortKey: 'BEST_SELLING', reverse: false },
      price_asc: { label: 'Price: Low to High', sortKey: 'PRICE', reverse: false },
      price_desc: { label: 'Price: High to Low', sortKey: 'PRICE', reverse: true },
      title_asc: { label: 'Name: A to Z', sortKey: 'TITLE', reverse: false },
      title_desc: { label: 'Name: Z to A', sortKey: 'TITLE', reverse: true },
      oldest: { label: 'Oldest', sortKey: 'CREATED_AT', reverse: false },
      newest: { label: 'Newest', sortKey: 'CREATED_AT', reverse: true },
    }
    : {
      recommended: { label: 'おすすめ', sortKey: 'BEST_SELLING', reverse: false },
      popularity: { label: '人気順', sortKey: 'BEST_SELLING', reverse: false },
      price_asc: { label: '価格が安い順', sortKey: 'PRICE', reverse: false },
      price_desc: { label: '価格が高い順', sortKey: 'PRICE', reverse: true },
      title_asc: { label: '商品名昇順', sortKey: 'TITLE', reverse: false },
      title_desc: { label: '商品名降順', sortKey: 'TITLE', reverse: true },
      oldest: { label: '古い順', sortKey: 'CREATED_AT', reverse: false },
      newest: { label: '新しい順', sortKey: 'CREATED_AT', reverse: true },
    };
  const DEFAULT_SORT = 'title_asc';
  // ============================================================
  // 1. URL解析: /collections/all/cl_イエロー+mo_ap2279+h1_sp
  //    → { collectionHandle: 'all', tags: ['cl_イエロー','mo_ap2279','h1_sp'] }
  //    タグが無い場合(/collections/all)は tags: [] になる
  // ============================================================
  function parseCollectionUrl(pathname) {
    const path = decodeURIComponent(pathname || window.location.pathname);

    if (!path.includes(CONFIG.collectionsBasePath)) {
      return { collectionHandle: null, tags: [] };
    }

    const after = path.split(CONFIG.collectionsBasePath)[1] || '';
    const segments = after.split('/').filter(Boolean);

    if (segments.length === 0) {
      return { collectionHandle: null, tags: [] };
    }

    const collectionHandle = segments[0];
    const tagSegment = segments[1] || '';
    let tags = tagSegment ? tagSegment.split('+').filter(Boolean) : [];

    if (!tags.length) {
      tags = parseQueryTags(window.location.search);
    }

    return { collectionHandle, tags };
  }

  function parseQueryTags(search) {
    const params = new URLSearchParams(search || '');
    const tags = [];

    params.getAll('tag').forEach(function (tag) {
      if (tag) tags.push(tag);
    });

    const tagsParam = params.get('tags');
    if (tagsParam) {
      tagsParam.split(/[,+\s]+/).forEach(function (tag) {
        if (tag) tags.push(tag);
      });
    }

    return tags;
  }

  // ============================================================
  // 1-2. URLから並び替え(sort)を取得する
  // ============================================================
  function getSortFromUrl(search) {
    const params = new URLSearchParams(search || window.location.search);
    const sortValue = params.get('sort');
    return SORT_OPTIONS[sortValue] ? sortValue : null;
  }

  // ============================================================
  // 1-3. URLに sort= を追記/更新する(ページ遷移なし)
  //    ・既存のクエリパラメータ(タグの ?tag= 等)は維持する
  //    ・パス部分(/collections/xxx や /collections/xxx/tag1+tag2)は変更しない
  // ============================================================
  function updateUrlSort(sortKey) {
    const url = new URL(window.location.href);

    if (sortKey === DEFAULT_SORT) {
      // デフォルト値の場合はURLを煩雑にしないため sort パラメータを外す
      url.searchParams.delete('sort');
    } else {
      url.searchParams.set('sort', sortKey);
    }

    window.history.replaceState({}, '', url.toString());
  }


  // ============================================================
  // 2. GraphQLクエリ組み立て(既存コードの書式 tag:\"xxx\" に合わせる)
  //    タグが無い場合は空文字を返す(= Storefront APIには無条件検索として渡す)
  // ============================================================
  function buildTagQueryString(tags) {
    if (!tags.length) return '';

    return tags.map((tag) => {
      const normalizedTag = String(tag).normalize('NFC');
      const decomposedTag = String(tag).normalize('NFD');
      const normalizedQuery = `tag:\\"${escapeQueryValue(normalizedTag)}\\"`;

      if (normalizedTag === decomposedTag) return normalizedQuery;

      return `(${normalizedQuery} OR tag:\\"${escapeQueryValue(decomposedTag)}\\")`;
    }).join(' AND ');
  }

  function escapeQueryValue(value) {
    return String(value).replace(/"/g, '\\"');
  }

  // ============================================================
  // 3. Storefront APIへのフェッチ
  //    既存の window.get_products_graphql があればそちらを優先して使う。
  //    (エンドポイント・認証・書式が本番実績のあるものと完全に一致する)
  //    無い場合のみ、同じ形式で独自にフォールバック実装する。
  // ============================================================
  function fetchProductsPage(query, cursor, sortKey, reverse) {
    if (typeof window.get_products_graphql === 'function') {
      return new Promise((resolve) => {
        window.get_products_graphql(
          query,
          function (data) {
            const products = data && data.products;
            resolve({
              edges: (products && products.edges) || [],
              hasNextPage: !!(products && products.pageInfo && products.pageInfo.hasNextPage),
            });
          },
          cursor || null,
          sortKey,
          reverse
        );
      });
    }

    return fetchProductsFallback(query, cursor, sortKey, reverse);
  }

  function fetchProductsFallback(query, cursor, sortKey, reverse) {

    const token = typeof theme_accessToken !== 'undefined' ? theme_accessToken : '';
    if (!token) {
      console.error('[collection-api-filter] theme_accessToken が見つかりません');
      return Promise.resolve({ edges: [], hasNextPage: false });
    }

    const cursorQuery = cursor ? ', after: "' + cursor + '"' : '';
    const sortQuery = sortKey ? ', sortKey: ' + sortKey : '';
    const reverseQuery = reverse ? ', reverse: true' : '';
    const contextDirective = window.caseplayMarketContext
      ? '@inContext(country: ' + window.caseplayMarketContext + ', language: ' + LANG_CODE + ')'
      : '@inContext(language: ' + LANG_CODE + ')';
    const body =
      'query ' + contextDirective + ' {' +
      ' products(query: "' + query + '", first: ' + CONFIG.perPage + cursorQuery + sortQuery + reverseQuery + ') {' +
      ' edges { cursor, node { ' +
      ' title, id, handle, tags, vendor, productType,' +
      ' priceRange { minVariantPrice { amount, currencyCode } },' +
      ' images(first:1) { edges { node { originalSrc } } }' +
      ' } },' +
      ' pageInfo { hasNextPage }' +
      ' } }';

    return fetch('/api/2024-07/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/graphql',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: body,
    })
      .then((res) => res.json())
      .then((json) => ({
        edges: (json.data && json.data.products && json.data.products.edges) || [],
        hasNextPage: !!(
          json.data &&
          json.data.products &&
          json.data.products.pageInfo &&
          json.data.products.pageInfo.hasNextPage
        ),
      }))
      .catch((err) => {
        console.error('[collection-api-filter] fetch失敗', err);
        return { edges: [], hasNextPage: false };
      });
  }

  // ============================================================
  // 4-1. 現在のフィルタータグとの一致判定
  // ============================================================
  function getMatchedFilterTags(productTags) {
    if (!state.tags.length || !productTags || !productTags.length) return [];
    return productTags.filter(function (tag) {
      return state.tags.indexOf(tag) !== -1;
    });
  }

  function sanitizeTagClass(tag) {
    // CSSクラス名として安全な形に変換する。
    // 日本語などのUnicode文字はそのまま残し、
    // クラス名として使えない記号(スペース・%・+など)だけハイフンに置換する。
    var safe = String(tag).replace(/[^a-zA-Z0-9_\-\u00C0-\uFFFF]/g, '-');
    return 'tag-' + safe;
  }

  // ============================================================
  // 4. タグ解析(既存 template_product-item.js のロジックを踏襲)
  // ============================================================
  const CASETYPE_FALLBACK_MAP = {
    h1_HY: 'Hybrid Case',
    h1_SP: 'スリムプロテクションケース',
    h1_FL: 'Folio Case',
    h1_HYSL: 'Hybrid Case with Sling',
    h1_FLSL: 'Folio Case with Sling',
    h1_SPLR: 'リボン付きケース',
  };

  function findTagByPrefix(tags, prefix) {
    return tags.find((t) => t.indexOf(prefix) === 0) || '';
  }

  function resolveDisplayFields(product, tagLabels) {
    const tags = product.tags || [];

    const modelTag = findTagByPrefix(tags, 'mo_');
    const caseTag = findTagByPrefix(tags, 'h1_');
    const productNameTag = findTagByPrefix(tags, 'productname_');
    const colTag = findTagByPrefix(tags, 'col_');

    let modelName = '';
    if (modelTag) {
      const modelCode = modelTag.split('_')[1];
      modelName =
        (window.fox && window.fox.settings && window.fox.settings.model
          ? window.fox.settings.model[modelCode]
          : '') || '';
    }

    let caseTypeName = '';
    if (caseTag) {
      caseTypeName =
        (window.fox && window.fox.settings && window.fox.settings.h1
          ? window.fox.settings.h1[caseTag]
          : '') ||
        CASETYPE_FALLBACK_MAP[caseTag] ||
        '';
    }

    const displayTitle = window.caseplayDisplayTitle(product.title, productNameTag);
    const displayTitleLabel = productNameTag && tagLabels && tagLabels[productNameTag];

    let genreLabel = '';
    if (colTag) {
      const genreMap = (window.caseplayGenreTranslations && window.caseplayGenreTranslations.en) || {};
      genreLabel = (tagLabels && tagLabels[colTag]) || (IS_EN && genreMap[colTag]) || colTag.replace('col_', '');
    }

    return { modelName, caseTypeName, displayTitle: displayTitleLabel || displayTitle, genreLabel };
  }

  // ============================================================
  // 5. DOM組み立て(既存 product-item-add の構造を再現)
  // ============================================================
  function getLocalePrefix() {
    const match = window.location.pathname.match(/^\/([a-z]{2}(-[a-z]{2})?)\//i);
    if (!match) return '';
    return '/' + match[1];
  }
  function renderProductCard(product, tagLabels) {
    const fields = resolveDisplayFields(product, tagLabels);
    const priceAmount =
      product.priceRange && product.priceRange.minVariantPrice
        ? product.priceRange.minVariantPrice.amount
        : null;
    const currencyCode =
      product.priceRange && product.priceRange.minVariantPrice && product.priceRange.minVariantPrice.currencyCode
        ? product.priceRange.minVariantPrice.currencyCode
        : 'JPY';

    const includeTaxesLabel = (window.caseplayTranslations && window.caseplayTranslations.includeTaxes) || '（税込）';
    const taxSuffix = window.Theme && !window.Theme.showTaxIncluded ? '' : includeTaxesLabel;

    let priceText = '';
    if (priceAmount) {
      const formatted = new Intl.NumberFormat(IS_EN ? 'en-US' : 'ja-JP', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: currencyCode === 'JPY' ? 0 : 2,
      }).format(Number(priceAmount));
      priceText = formatted + taxSuffix + '〜';
    }

    const imageEdge = product.images && product.images.edges && product.images.edges[0];
    const imageUrl = imageEdge ? imageEdge.node.originalSrc : '';
    const url = getLocalePrefix() + '/products/' + product.handle;

    // ------------------------------------------------------------
    // 現在選択中のフィルタータグとの一致判定
    // ------------------------------------------------------------
    const matchedTags = getMatchedFilterTags(product.tags || []);
    const matchedTagClasses = matchedTags.map(sanitizeTagClass);

    const classList = ['product-item', 'js-section-product', 'product-all__item'];
    if (matchedTags.length) {
      classList.push('is-filter-matched'); // 汎用の「一致した」クラス
      classList.push.apply(classList, matchedTagClasses); // タグ単位のクラス(例: tag-cl_イエロー)
    }

    const article = document.createElement('article');
    article.className = classList.join(' ');
    article.dataset.productTags = (product.tags || []).join(',');
    article.dataset.productItemTitle = product.title;
    article.dataset.productItemHandle = product.handle;
    if (matchedTags.length) {
      article.dataset.matchedTags = matchedTags.join(',');
    }

    article.innerHTML = `
      <a href="${url}" class="product-item-image-link" tabindex="-1">
        ${imageUrl ? `<img src="${imageUrl}" alt="${escapeHtml(product.title)}" loading="lazy">` : ''}
      </a>
      <a class="product-item-details" href="${url}">
        <p class="collection__product-item-category">${escapeHtml(fields.caseTypeName)}</p>
        <p class="collection__product-item-subtitle">${escapeHtml(fields.genreLabel)}</p>
        <h4 class="collection__product-item-title product-item-title js-item-title">
          ${escapeHtml(fields.displayTitle)}
        </h4>
        <p class="collection__product-item-price product-item-price">${priceText}</p>
      </a>
    `;

    return article;
  }

  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }

  // ============================================================
  // 6. メイン制御(初回取得 + IntersectionObserverによる無限スクロール)
  //    タグの有無にかかわらず動作する
  // ============================================================
  const state = {
    tags: [],
    query: '',
    cursor: null,
    hasNextPage: false,
    loading: false,
    sort: DEFAULT_SORT,
    requestId: 0,
  };

  let observer = null;

  function createSentinel(wrapper) {
    // 画面外の監視用要素をグリッドの直後に1つ用意する。
    // これが画面内に入ったタイミングで次ページを読み込む。
    let sentinel = document.querySelector('[data-scroll-sentinel]');
    if (!sentinel) {
      sentinel = document.createElement('div');
      sentinel.setAttribute('data-scroll-sentinel', '');
      sentinel.style.width = '100%';
      sentinel.style.height = '1px';
      wrapper.insertAdjacentElement('afterend', sentinel);
    }
    return sentinel;
  }

  function setupObserver(wrapper) {
    const sentinel = createSentinel(wrapper);

    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && !state.loading && state.hasNextPage) {
            loadPage(wrapper, { reset: false });
          }
        });
      },
      {
        root: null,
        rootMargin: '400px 0px', // 画面下部に到達する少し手前で先読みする
        threshold: 0,
      }
    );

    observer.observe(sentinel);
  }

  function toggleLoadingIndicator(isLoading) {
    let indicator = document.querySelector('[data-scroll-loading]');
    if (isLoading) {
      if (!indicator) {
        const sentinel = document.querySelector('[data-scroll-sentinel]');
        indicator = document.createElement('div');
        indicator.setAttribute('data-scroll-loading', '');
        indicator.className = 'collection-loading';
        indicator.textContent = '読み込み中...';
        if (sentinel) {
          sentinel.insertAdjacentElement('afterend', indicator);
        }
      }
    } else if (indicator) {
      indicator.remove();
    }
  }

  function setupSortSelect(wrapper) {
    // すでに専用のセレクトがページ内にあればそれを使う。無ければ簡易的に生成する。
    let select = document.querySelector('[data-collection-sort]');
    if (!select) {
      select = document.createElement('select');
      select.setAttribute('data-collection-sort', '');
      Object.keys(SORT_OPTIONS).forEach(function (key) {
        const opt = document.createElement('option');
        opt.value = key;
        opt.textContent = SORT_OPTIONS[key].label;
        select.appendChild(opt);
      });
      wrapper.insertAdjacentElement('beforebegin', select);
    }

    select.value = state.sort;

    select.addEventListener('change', function () {
      const nextSort = SORT_OPTIONS[select.value] ? select.value : DEFAULT_SORT;
      if (nextSort === state.sort) return;

      state.sort = nextSort;
      state.cursor = null;
      state.hasNextPage = false;

      updateUrlSort(nextSort);
      if (observer) {
        observer.disconnect();
        observer = null;
      }

      wrapper.innerHTML = '<div class="collection-loading">読み込み中...</div>';
      loadPage(wrapper, { reset: true }).then(function (rendered) {
        if (rendered) setupObserver(wrapper);
      });
    });
  }


  function init() {
    const wrapper = document.querySelector('[data-product-item-wrapper]');
    if (!wrapper) return;

    const parsed = parseCollectionUrl(window.location.pathname);
    if (!parsed.collectionHandle) return;

    state.tags = parsed.tags;
    state.query = buildTagQueryString(parsed.tags);

    const sortFromUrl = getSortFromUrl(window.location.search);
    if (sortFromUrl) {
      state.sort = sortFromUrl;
    }

    setupSortSelect(wrapper);

    wrapper.innerHTML = '<div class="collection-loading">読み込み中...</div>';
    loadPage(wrapper, { reset: true }).then(function (rendered) {
      if (rendered) setupObserver(wrapper);
    });
  }

  function loadPage(wrapper, options) {
    const reset = options && options.reset;
    if (state.loading && !reset) return Promise.resolve(false);
    state.loading = true;
    const requestId = ++state.requestId;
    toggleLoadingIndicator(true);

    const sortConfig = SORT_OPTIONS[state.sort] || SORT_OPTIONS[DEFAULT_SORT];

    return fetchProductsPage(
      state.query,
      reset ? null : state.cursor,
      sortConfig.sortKey,
      sortConfig.reverse
    ).then(function (result) {
      if (requestId !== state.requestId) return false;
      const edges = result.edges;
      const hasNextPage = result.hasNextPage;
      const labelTags = [];

      edges.forEach(function (edge) {
        ['productname_', 'col_'].forEach(function (prefix) {
          const tag = findTagByPrefix(edge.node.tags || [], prefix);
          if (tag && labelTags.indexOf(tag) === -1) labelTags.push(tag);
        });
      });

      return window.caseplayTagLabels.get(labelTags).then(function (tagLabels) {
        if (requestId !== state.requestId) return false;

        if (reset) {
          wrapper.innerHTML = '';
        }

        if (!edges.length && reset) {
          wrapper.innerHTML =
            '<div class="collection-empty grid-item-100" data-collection-empty>' +
            '該当する商品が見つかりませんでした</div>';
        } else {
          edges.forEach(function (edge) {
            wrapper.appendChild(renderProductCard(edge.node, tagLabels));
          });
        }

        if (edges.length) {
          state.cursor = edges[edges.length - 1].cursor;
        }
        state.hasNextPage = hasNextPage;

        // 次ページが無くなったら監視をやめる(無駄な発火を防ぐ)
        if (!state.hasNextPage && observer) {
          observer.disconnect();
        }

        return true;
      });
    }).catch(function () {
      return false;
    }).then(function (rendered) {
      if (requestId === state.requestId) {
        state.loading = false;
        toggleLoadingIndicator(false);
      }
      return rendered;
    });
  }

  // ============================================================
  // 実行
  // ============================================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();


